import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Megaphone, ExternalLink } from 'lucide-react';
import { trackAdImpression, trackAdClick, detectDeviceType } from '../services/adService';
import { useTheme } from '../contexts/ThemeContext';

interface AdSpaceProps {
  id?: string; // ID do anúncio no banco
  title?: string;
  description?: string;
  imageUrl?: string;
  ctaText?: string;
  ctaUrl?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'premium';
  index?: number;
}

const AdSpace: React.FC<AdSpaceProps> = ({
  id,
  title = 'Espaço Publicitário',
  description = 'Anuncie aqui e alcance milhares de pessoas interessadas em finanças.',
  imageUrl,
  ctaText = 'Saiba mais',
  ctaUrl = '#',
  size = 'medium',
  variant = 'default',
  index = 0
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [impressionId, setImpressionId] = useState<string | null>(null);

  // Registra impressão quando o componente é montado
  useEffect(() => {
    if (id) {
      const recordImpression = async () => {
        const deviceType = detectDeviceType();
        const impId = await trackAdImpression(id, undefined, deviceType);
        if (impId) {
          setImpressionId(impId);
        }
      };
      recordImpression();
    }
  }, [id]);

  // Handler de clique
  const handleClick = async () => {
    if (impressionId) {
      await trackAdClick(impressionId);
    }
  };
  const sizeClasses = {
    small: 'h-32',
    medium: 'h-48',
    large: 'h-64'
  };

  const isPremium = variant === 'premium';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1 }}
      className={`relative rounded-xl overflow-hidden border backdrop-blur-sm group hover:scale-[1.02] transition-all duration-300 ${sizeClasses[size]} ${
        isPremium
          ? isDark
            ? 'bg-gradient-to-br from-yellow-900/30 via-orange-900/20 to-slate-900 border-yellow-500/50 shadow-xl shadow-yellow-500/20'
            : 'bg-gradient-to-br from-yellow-50 via-orange-50 to-white border-yellow-300 shadow-xl shadow-yellow-200/40'
          : isDark
            ? 'bg-slate-800/40 border-slate-700/50'
            : 'bg-white border-teal-200 shadow-md'
      }`}
    >
      {/* Badge Premium */}
      {isPremium && (
        <div className="absolute top-3 right-3 z-10 px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-black rounded-full shadow-lg">
          ⭐ DESTAQUE
        </div>
      )}

      {/* Background Image */}
      {imageUrl ? (
        <>
          <img 
            src={imageUrl} 
            alt={title}
            className={`absolute inset-0 w-full h-full object-cover group-hover:opacity-40 transition-opacity ${
              isDark ? 'opacity-30' : 'opacity-20'
            }`}
          />
          <div className={`absolute inset-0 ${
            isDark 
              ? 'bg-gradient-to-t from-slate-900 via-slate-900/80 to-slate-900/50'
              : 'bg-gradient-to-t from-white via-white/80 to-white/50'
          }`} />
        </>
      ) : (
        <div className={`absolute inset-0 ${
          isDark 
            ? 'bg-gradient-to-br from-slate-800/50 to-slate-900/50'
            : 'bg-gradient-to-br from-slate-50 to-white'
        }`} />
      )}

      {/* Content */}
      <div className="relative h-full flex flex-col justify-between p-5">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Megaphone size={20} className={
              isPremium 
                ? 'text-yellow-400' 
                : isDark ? 'text-slate-400' : 'text-slate-500'
            } />
            <span className={`text-xs font-bold uppercase tracking-wider ${
              isDark ? 'text-slate-400' : 'text-slate-500'
            }`}>
              Publicidade
            </span>
          </div>
          
          <h4 className={`text-lg font-bold mb-2 ${
            isPremium 
              ? isDark ? 'text-white' : 'text-slate-900'
              : isDark ? 'text-slate-200' : 'text-slate-800'
          }`}>
            {title}
          </h4>
          
          <p className={`text-sm line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            {description}
          </p>
        </div>

        {/* CTA */}
        <a
          href={ctaUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={handleClick}
          className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
            isPremium
              ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:shadow-lg hover:shadow-yellow-500/50'
              : isDark
                ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                : 'bg-teal-500 text-white hover:bg-teal-600'
          }`}
        >
          {ctaText}
          <ExternalLink size={14} />
        </a>
      </div>
    </motion.div>
  );
};

export default AdSpace;
